import { CodingAgentType, PromptModel, WorkflowNodeType } from 'api/model.ts';

export type OfflineAvailability = 'unavailable' | 'local-model-required';

type NodeRestriction = {
  availability: OfflineAvailability;
  reason: string;
  isConfigurationCompatible?: (configuration: unknown) => boolean;
};

const REMOTE_LLM_REASON = 'Calls a remote LLM provider. Available offline if a local model is selected.';
const REMOTE_NETWORK_REASON = 'Requires network access.';
const REMOTE_GIT_REASON = 'Requires access to a remote Git repository.';
const REMOTE_RESEARCH_REASON = 'Uses Perplexity for remote web research.';
const CODING_AGENT_REASON = 'Calls a remote LLM provider. Available offline when OpenCode is used with a local model.';

export const OFFLINE_UNAVAILABLE_NODES: Partial<Record<WorkflowNodeType, NodeRestriction>> = {
  data_loader_github_project: { availability: 'unavailable', reason: REMOTE_GIT_REASON },
  data_loader_llm_research: { availability: 'unavailable', reason: REMOTE_RESEARCH_REASON },

  llm_prompt: { availability: 'local-model-required', reason: REMOTE_LLM_REASON },
  llm_extraction: { availability: 'local-model-required', reason: REMOTE_LLM_REASON },
  llm_classify: { availability: 'local-model-required', reason: REMOTE_LLM_REASON },
  llm_summarize: { availability: 'local-model-required', reason: REMOTE_LLM_REASON },
  llm_map_reduce: { availability: 'local-model-required', reason: REMOTE_LLM_REASON },
  llm_count_tokens: { availability: 'local-model-required', reason: REMOTE_LLM_REASON },
  llm_research: { availability: 'unavailable', reason: REMOTE_RESEARCH_REASON },
  coding_agent: {
    availability: 'local-model-required',
    reason: CODING_AGENT_REASON,
    isConfigurationCompatible: (cfg) =>
      (cfg as { agent_type?: string } | undefined)?.agent_type === CodingAgentType.OPENCODE,
  },

  web_scraping: { availability: 'unavailable', reason: REMOTE_NETWORK_REASON },

  git_export: { availability: 'unavailable', reason: REMOTE_GIT_REASON },
  github_wiki_page_upload: { availability: 'unavailable', reason: REMOTE_GIT_REASON },

  legacy_llm_count_tokens: { availability: 'local-model-required', reason: REMOTE_LLM_REASON },
  multimodal_gemini_prompt: { availability: 'unavailable', reason: 'Uses Gemini for a multimodal prompt.' },
};

export const getNodeOfflineRestriction = (type: WorkflowNodeType): NodeRestriction | undefined =>
  OFFLINE_UNAVAILABLE_NODES[type];

type FieldRestriction = {
  title: string;
  reason: string;
};

export const OFFLINE_UNAVAILABLE_FIELDS: Partial<Record<WorkflowNodeType, Record<string, FieldRestriction>>> = {};

export const getFieldOfflineRestriction = (type: WorkflowNodeType, fieldName: string): FieldRestriction | undefined =>
  OFFLINE_UNAVAILABLE_FIELDS[type]?.[fieldName];

const isNonEmptyRestrictedValue = (value: unknown): boolean => {
  if (value == null) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'string') return value.trim() !== '';
  if (typeof value === 'boolean') return value;
  return true;
};

export const getOfflineUnavailableField = (
  type: WorkflowNodeType,
  configuration: unknown,
): { fieldName: string; title: string; reason: string } | undefined => {
  const fields = OFFLINE_UNAVAILABLE_FIELDS[type];
  if (!fields || configuration == null || typeof configuration !== 'object') return undefined;
  for (const [fieldName, { title, reason }] of Object.entries(fields)) {
    if (isNonEmptyRestrictedValue((configuration as Record<string, unknown>)[fieldName])) {
      return { fieldName, title, reason };
    }
  }
  return undefined;
};

export type OfflineNodeIssueSource = 'node-unavailable' | 'node-conditional' | 'field-unavailable';

export type OfflineNodeIssue = {
  source: OfflineNodeIssueSource;
  reason: string;
};

export const getOfflineNodeIssue = (
  type: WorkflowNodeType,
  configuration: unknown,
  models?: PromptModel[],
): OfflineNodeIssue | undefined => {
  const restriction = OFFLINE_UNAVAILABLE_NODES[type];
  if (restriction?.availability === 'unavailable') {
    return { source: 'node-unavailable', reason: restriction.reason };
  }
  if (restriction?.availability === 'local-model-required') {
    const modelId = (configuration as { model_id?: string } | undefined)?.model_id;
    const model = modelId ? models?.find((m) => m.id === modelId) : undefined;
    if (!model?.is_local) {
      return { source: 'node-conditional', reason: restriction.reason };
    }
  }
  if (restriction?.isConfigurationCompatible && !restriction.isConfigurationCompatible(configuration)) {
    return { source: 'node-conditional', reason: restriction.reason };
  }
  const fieldIssue = getOfflineUnavailableField(type, configuration);
  if (fieldIssue) {
    return { source: 'field-unavailable', reason: fieldIssue.reason };
  }
  return undefined;
};
